/**
 * Service client pour l'envoi des logs au backend
 * Utilisé pour tracer les actions utilisateur dans l'application
 */

/**
 * Envoie un log d'action au backend
 * @param {string} action - Description de l'action (ex: "Rubrique ajoutée - Type vierge")
 * @param {string|null} reportNumber - Numéro du rapport au format "XXXXXX/YYYY" ou null
 * @param {Object} content - Détails supplémentaires de l'action
 * @returns {Promise<void>}
 */
export const logAction = async (action, reportNumber = null, content = {}) => {
  try {
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      // Pas de token, l'utilisateur n'est pas connecté
      return;
    }

    await fetch('/api/logs/action', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        action,
        reportNumber,
        content
      })
    });
  } catch (error) {
    // Ne jamais faire échouer l'application à cause des logs
    console.warn('Erreur lors de l\'envoi du log:', error.message);
  }
};

/**
 * Formate un numéro de rapport pour les logs
 * @param {string|number} number - Numéro du PV (6 chiffres)
 * @param {string|number} year - Année (4 chiffres)
 * @returns {string} - Format "XXXXXX/YYYY"
 */
export const formatReportNumber = (number, year) => {
  if (!number || !year) return null;
  // Formater le numéro avec 6 chiffres et l'année avec 4 chiffres
  const formattedNumber = String(number).padStart(6, '0');
  const formattedYear = String(year).padStart(4, '0');
  return `${formattedNumber}/${formattedYear}`;
};

/**
 * Actions prédéfinies pour une cohérence dans les logs
 */
export const LOG_ACTIONS = {
  // Rapport
  REPORT_CREATE: 'Création du procès-verbal',
  REPORT_ENTER: 'Entrée dans le procès-verbal',
  REPORT_LEAVE: 'Sortie du procès-verbal',
  REPORT_DELETE: 'Suppression du procès-verbal',
  REPORT_DOWNLOAD: 'Téléchargement du procès-verbal',
  REPORT_MODE_CHANGE: 'Changement de mode',
  
  // Historique
  HISTORY_VIEW: 'Consultation de l\'historique',
  HISTORY_RESTORE: 'Restauration d\'une version',
  
  // Rubriques
  RUBRIC_ADD_EMPTY: 'Ajout rubrique vierge',
  RUBRIC_ADD_GUARDIA: 'Ajout rubrique GuardIA',
  RUBRIC_ADD_TEMPLATE: 'Ajout rubrique depuis modèle',
  RUBRIC_ADD_PERSONAL: 'Ajout rubrique personnelle',
  RUBRIC_ADD_GROUP: 'Ajout groupe de rubriques',
  RUBRIC_DELETE: 'Suppression de rubrique',
  RUBRIC_MOVE: 'Déplacement de rubrique',
  RUBRIC_EDIT_TITLE: 'Modification du titre',
  RUBRIC_EDIT_CONTENT: 'Modification du contenu',
  RUBRIC_EDIT_CANCEL: 'Annulation édition rubrique',
  RUBRIC_EDIT_NO_CHANGE: 'Enregistrement sans modification',
  RUBRIC_GENERATE: 'Génération de rubrique',
  
  // Commentaires
  COMMENT_ADD: 'Ajout de commentaire',
  COMMENT_EDIT: 'Modification de commentaire',
  COMMENT_DELETE: 'Suppression de commentaire',
  
  // Rubriques - Consultation
  RUBRIC_VIEW: 'Consultation de rubrique',
  
  // Éléments - Import
  ELEMENTS_IMPORT: 'Importation d\'éléments',
  ELEMENTS_REPRISE: 'Reprise d\'éléments',
  
  // Éléments - Structure
  ELEMENTS_STRUCTURE_UPDATE: 'Modification structure PV',
  ELEMENTS_REDACTION_UPDATE: 'Modification dates rédaction',
  ELEMENTS_UNIT_UPDATE: 'Modification service',
  
  // Éléments - Intervenants
  ELEMENTS_INTERVENANT_ADD: 'Ajout intervenant',
  ELEMENTS_INTERVENANT_EDIT: 'Modification intervenant',
  ELEMENTS_INTERVENANT_DELETE: 'Suppression intervenant',
  
  // Éléments - Faits
  ELEMENTS_FAIT_ADD: 'Ajout fait',
  ELEMENTS_FAIT_EDIT: 'Modification fait',
  ELEMENTS_FAIT_DELETE: 'Suppression fait',
  
  // Éléments - Personnes physiques
  ELEMENTS_PERSONNE_ADD: 'Ajout personne physique',
  ELEMENTS_PERSONNE_EDIT: 'Modification personne physique',
  ELEMENTS_PERSONNE_DELETE: 'Suppression personne physique',
  
  // Éléments - Personnes morales
  ELEMENTS_MORALE_ADD: 'Ajout personne morale',
  ELEMENTS_MORALE_EDIT: 'Modification personne morale',
  ELEMENTS_MORALE_DELETE: 'Suppression personne morale',
  
  // Éléments - Véhicules
  ELEMENTS_VEHICULE_ADD: 'Ajout véhicule',
  ELEMENTS_VEHICULE_EDIT: 'Modification véhicule',
  ELEMENTS_VEHICULE_DELETE: 'Suppression véhicule',
  
  // Éléments - Objets
  ELEMENTS_OBJET_ADD: 'Ajout objet',
  ELEMENTS_OBJET_EDIT: 'Modification objet',
  ELEMENTS_OBJET_DELETE: 'Suppression objet',
  
  // Éléments - Copies presse-papier
  ELEMENTS_COPY: 'Copie élément presse-papier',
  
  // Insertions
  INSERTION_ADD: 'Ajout d\'insertion',
  INSERTION_EDIT: 'Modification d\'insertion',
  INSERTION_DELETE: 'Suppression d\'insertion',
  INSERTION_COPY: 'Copie insertion presse-papier',
  
  // Documents
  DOCUMENT_DOWNLOAD: 'Téléchargement document zonal',
  
  // Annexes
  ANNEXE_PHOTO_DOC: 'Création dossier photographique',
  ANNEXE_CREATE: 'Création d\'annexe',
  
  // Outils IA
  GUARDIA_CHAT: 'Utilisation GuardIA Chat',
  GUARDIA_AUDIO: 'Utilisation GuardIA Audio',
  GUARDIA_VISION: 'Utilisation GuardIA Vision',
  GUARDIA_WRITE: 'Utilisation GuardIA Write',
  GUARDIA_CHECK: 'Utilisation GuardIA Check'
};

export default {
  logAction,
  formatReportNumber,
  LOG_ACTIONS
};
